import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Percent, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import AppLayout from '../components/layout/AppLayout';
import { fetchProviderFeeConfigs } from '../services/api';
import MethodCell from './providerFees/MethodCell';
import type {
  EditableRow,
  ProviderFeeItem,
  ProviderFeeOperation,
  SupportedByKeyMap,
  SupportedMethod,
} from './providerFees/types';
import {
  formatProviderDisplayName,
  fillHardcodedSupportedCatalog,
  providerFeeRowId,
  toPercentString,
  toRateNumber,
  toSupportedKey,
  formatNetworkFeeDisplay,
  networkFeeFieldsFromItem,
} from './providerFees/utils';

function supportedMethodsFromApi(
  methods: Array<{ methodId?: string; id?: string; name?: string; ticker?: string; currency?: string }>
): SupportedMethod[] {
  if (!Array.isArray(methods)) return [];
  const out: SupportedMethod[] = [];
  for (const m of methods) {
    const methodId = String(m.methodId || m.id || '').trim();
    if (!methodId) continue;
    out.push({
      methodId,
      id: m.id || methodId,
      name: m.name,
      ticker: m.ticker,
      currency: m.currency,
    });
  }
  return out;
}

function supportedByKeyFromCatalogResponse(
  raw: Record<string, { methods?: Array<{ methodId?: string; id?: string; name?: string; ticker?: string; currency?: string }> }> | undefined
): SupportedByKeyMap {
  const out: SupportedByKeyMap = {};
  if (raw && typeof raw === 'object') {
    for (const [key, entry] of Object.entries(raw)) {
      out[key] = supportedMethodsFromApi(Array.isArray(entry?.methods) ? entry.methods : []);
    }
  }
  return fillHardcodedSupportedCatalog(out);
}

function buildRowsFromServer(
  serverItems: ProviderFeeItem[],
  supportedByKey: SupportedByKeyMap
): EditableRow[] {
  const makeCatalogStub = (
    provider: string,
    operation: ProviderFeeOperation,
    methodId: string,
    template?: ProviderFeeItem
  ): ProviderFeeItem => ({
    id: template?.id ?? '',
    provider: template?.provider ?? provider,
    operation: template?.operation ?? operation,
    methodId,
    paymentGateFeeRate: template?.paymentGateFeeRate ?? 0,
    systemFeeRate: template?.systemFeeRate ?? 0,
    feeRate: template?.feeRate ?? 0,
    isEnabled: template?.isEnabled ?? true,
  });

  const exactByKey = new Map<string, ProviderFeeItem>();
  const fallbackByProviderOp = new Map<string, ProviderFeeItem>();

  for (const it of serverItems) {
    const provider = (it.provider || '').toLowerCase();
    const operation = (it.operation || '').toLowerCase();
    const methodId = (it.methodId || 'all').toLowerCase();
    exactByKey.set(`${provider}::${operation}::${methodId}`, it);
    if (methodId === 'all') {
      fallbackByProviderOp.set(`${provider}::${operation}`, it);
    }
  }

  const mapped: EditableRow[] = [];
  const seen = new Set<string>();

  const pushRow = (
    it: ProviderFeeItem,
    methodIdOverride?: string,
    opts?: { requireInCatalog?: boolean }
  ) => {
    const provider = (it.provider || '').toLowerCase();
    const operation = (it.operation || '').toLowerCase();
    const methodId = (methodIdOverride || it.methodId || 'all').toLowerCase();
    if (provider === 'online_debt') return;
    if ((operation === 'deposit' || operation === 'withdrawal') && methodId === 'all') return;
    if (
      provider === 'btcpay' &&
      operation === 'withdrawal' &&
      !['bitcoin_transfer', 'litecoin_transfer', 'dogecoin_transfer'].includes(methodId)
    ) {
      return;
    }
    if (opts?.requireInCatalog && (operation === 'deposit' || operation === 'withdrawal')) {
      const supported = supportedByKey[toSupportedKey(provider, operation)] || [];
      const supportedIds = new Set(
        supported
          .map((m) => String(m.methodId || m.id || '').trim().toLowerCase())
          .filter(Boolean)
      );
      if (supportedIds.size > 0 && methodId !== 'all' && !supportedIds.has(methodId)) {
        return;
      }
    }

    const rowKey = `${provider}::${operation}::${methodId}`;
    if (seen.has(rowKey)) return;
    seen.add(rowKey);

    const hasSplit = it.paymentGateFeeRate != null || it.systemFeeRate != null;
    const gateRate = hasSplit ? (it.paymentGateFeeRate ?? 0) : (it.feeRate ?? 0);
    const sysRate = hasSplit ? (it.systemFeeRate ?? 0) : 0;
    mapped.push({
      provider,
      operation: it.operation,
      methodId,
      isEnabled: true,
      paymentGateFeePercent: toPercentString(gateRate),
      systemFeePercent: toPercentString(sysRate),
      ...networkFeeFieldsFromItem(it),
    });
  };

  for (const it of serverItems) {
    pushRow(it);
  }

  for (const [providerOp, fallback] of Array.from(fallbackByProviderOp.entries())) {
    const [provider, operation] = providerOp.split('::');
    if (operation !== 'deposit' && operation !== 'withdrawal') continue;
    const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
    for (const m of methods) {
      const mid = String(m.methodId || m.id || '').trim().toLowerCase();
      if (!mid) continue;
      const exact = exactByKey.get(`${provider}::${operation}::${mid}`);
      pushRow(exact || fallback, mid, { requireInCatalog: true });
    }
  }

  const catalogProviders = ['btcpay', 'linkmepay', 'ggusonepay', 'meld', 'zeroxprocessing'];
  for (const provider of catalogProviders) {
    for (const operation of ['deposit', 'withdrawal'] as const) {
      const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
      if (methods.length === 0) continue;
      const fallback = fallbackByProviderOp.get(`${provider}::${operation}`);
      for (const m of methods) {
        const mid = String(m.methodId || m.id || '').trim().toLowerCase();
        if (!mid) continue;
        const exact = exactByKey.get(`${provider}::${operation}::${mid}`);
        if (exact) continue;
        pushRow(makeCatalogStub(provider, operation, mid, fallback), mid, { requireInCatalog: true });
      }
    }
  }

  return mapped;
}

function formatTotalFeeDisplay(row: EditableRow): string {
  const basePct = toRateNumber(row.paymentGateFeePercent) + toRateNumber(row.systemFeePercent);
  const baseLabel = `${toPercentString(basePct)}%`;
  if (row.networkFeeType === 'fixed' && Number(row.networkFeeFixedUsd) > 0) {
    return `${baseLabel} + $${row.networkFeeFixedUsd}`;
  }
  if (row.networkFeeType === 'percent' && Number(row.networkFeePercent) > 0) {
    return `${baseLabel} + network`;
  }
  return baseLabel;
}

type FeeSortField = 'gateway' | 'network' | 'system' | 'total';
type FeeSortDir = 'asc' | 'desc';

function feeNetworkSortValue(row: EditableRow): number {
  if (row.networkFeeType === 'fixed') return Number(row.networkFeeFixedUsd) || 0;
  if (row.networkFeeType === 'percent') return toRateNumber(row.networkFeePercent);
  return 0;
}

function feeTotalSortValue(row: EditableRow): number {
  const base = toRateNumber(row.paymentGateFeePercent) + toRateNumber(row.systemFeePercent);
  if (row.networkFeeType === 'percent') return base + toRateNumber(row.networkFeePercent);
  // Fixed network fee is USD — keep percent base primary; add fixed as secondary weight
  if (row.networkFeeType === 'fixed') return base + (Number(row.networkFeeFixedUsd) || 0) / 1000;
  return base;
}

function FeeSortTh({
  label,
  field,
  sort,
  onSort,
  className,
}: {
  label: string;
  field: FeeSortField;
  sort: { by: FeeSortField; dir: FeeSortDir };
  onSort: (f: FeeSortField) => void;
  className?: string;
}) {
  const active = sort.by === field;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex w-full items-center justify-end gap-1 font-medium hover:text-stone-800"
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className="text-[10px] text-stone-400" aria-hidden>
          {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

const FEE_RESPONSIBILITY_ITEMS = [
  {
    index: '01',
    title: 'Player / Shop',
    from: 'Player',
    fromDescription: 'End user',
    to: 'Shop',
    toDescription: 'Player-facing agent',
    payer: 'Shop pays all fees',
    accentClassName: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  {
    index: '02',
    title: 'Player / Platform',
    from: 'Player',
    fromDescription: 'End user',
    to: 'Platform',
    toDescription: 'System',
    payer: 'Platform pays all fees',
    accentClassName: 'border-violet-200 bg-violet-50 text-violet-800',
  },
  {
    index: '03',
    title: 'Platform / Agent Network',
    from: 'Platform',
    fromDescription: 'System',
    to: 'Agent Network',
    toDescription: 'Super Agent, Agent, Shop',
    payer: 'Agent side pays all fees. Platform pays none.',
    accentClassName: 'border-orange-200 bg-orange-50 text-orange-800',
  },
] as const;

function FeeResponsibilityGuide() {
  return (
    <section className="border-b border-stone-200 bg-stone-50/60 px-4 py-5 sm:px-6">
      <div className="mb-3">
        <p className="text-sm font-semibold text-stone-900">Transaction & Fee Responsibility</p>
        <p className="text-xs text-stone-500">
          Online transactions between entities and who pays the configured fees.
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {FEE_RESPONSIBILITY_ITEMS.map((item) => (
          <div key={item.index} className="rounded-lg border border-stone-200 bg-white p-3">
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded-md bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-500">
                {item.index}
              </span>
              <span className="text-sm font-semibold text-stone-900">{item.title}</span>
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <div className="rounded-md border border-blue-100 bg-blue-50 p-2">
                <p className="text-sm font-semibold text-blue-900">{item.from}</p>
                <p className="text-xs text-blue-700">{item.fromDescription}</p>
              </div>
              <span className="text-lg font-semibold text-stone-400" aria-label="Transaction relationship">
                ↔
              </span>
              <div className="rounded-md border border-amber-100 bg-amber-50 p-2">
                <p className="text-sm font-semibold text-amber-900">{item.to}</p>
                <p className="text-xs text-amber-700">{item.toDescription}</p>
              </div>
            </div>
            <div className={`mt-3 rounded-md border px-2 py-1.5 text-xs font-semibold ${item.accentClassName}`}>
              {item.payer}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-stone-500">
        Agent network payout fees use the selected payout provider and withdrawal method from this configuration.
      </p>
    </section>
  );
}

export default function ProviderFeesPage() {
  const [serverItems, setServerItems] = useState<ProviderFeeItem[]>([]);
  const [supportedByKey, setSupportedByKey] = useState<SupportedByKeyMap>({});
  const [loading, setLoading] = useState(true);
  const [activeOperationTab, setActiveOperationTab] = useState<'deposit' | 'withdrawal'>('deposit');
  const [feeSort, setFeeSort] = useState<{ by: FeeSortField; dir: FeeSortDir }>({
    by: 'total',
    dir: 'desc',
  });
  const loadGenRef = useRef(0);

  const rows = useMemo(
    () => buildRowsFromServer(serverItems, supportedByKey),
    [serverItems, supportedByKey]
  );

  const loadPageData = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    try {
      const res = await fetchProviderFeeConfigs();
      if (gen !== loadGenRef.current) return;
      if (!res.success) {
        toast.error(res.message || 'Failed to load provider fees');
        return;
      }
      const items = (res.data?.items || []) as ProviderFeeItem[];
      if (gen !== loadGenRef.current) return;
      setServerItems(items);
      setSupportedByKey(supportedByKeyFromCatalogResponse(res.data?.supportedCatalog));
    } catch (err: any) {
      if (gen !== loadGenRef.current) return;
      toast.error(err?.response?.data?.message || 'Failed to load provider fees');
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadPageData();
    return () => {
      loadGenRef.current += 1;
    };
  }, [loadPageData]);

  const filteredRows = useMemo(() => {
    if (activeOperationTab === 'withdrawal') {
      return rows.filter((r) => r.operation === 'withdrawal');
    }
    return rows.filter((r) => r.operation === 'deposit');
  }, [rows, activeOperationTab]);

  const groupedRowsByProvider = useMemo(() => {
    const groups = new Map<string, EditableRow[]>();
    for (const row of filteredRows) {
      const key = (row.provider || '').toLowerCase() || 'unknown';
      const prev = groups.get(key) || [];
      prev.push(row);
      groups.set(key, prev);
    }
    const dir = feeSort.dir === 'asc' ? 1 : -1;
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([provider, providerRows]) => {
        const sorted = [...providerRows].sort((a, b) => {
          let cmp = 0;
          if (feeSort.by === 'gateway') {
            cmp = toRateNumber(a.paymentGateFeePercent) - toRateNumber(b.paymentGateFeePercent);
          } else if (feeSort.by === 'network') {
            cmp = feeNetworkSortValue(a) - feeNetworkSortValue(b);
          } else if (feeSort.by === 'system') {
            cmp = toRateNumber(a.systemFeePercent) - toRateNumber(b.systemFeePercent);
          } else {
            cmp = feeTotalSortValue(a) - feeTotalSortValue(b);
          }
          if (cmp === 0) cmp = (a.methodId || '').localeCompare(b.methodId || '');
          return cmp * dir;
        });
        return [provider, sorted] as [string, EditableRow[]];
      });
  }, [filteredRows, feeSort]);

  const toggleFeeSort = (field: FeeSortField) => {
    setFeeSort((prev) => ({
      by: field,
      dir: prev.by === field ? (prev.dir === 'desc' ? 'asc' : 'desc') : 'desc',
    }));
  };

  const emptyMessage =
    rows.length === 0
      ? 'No rows yet.'
      : filteredRows.length === 0
        ? 'No methods for this tab.'
        : 'No results.';

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-6xl">
        <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-stone-200 px-4 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
            <div className="space-y-1">
              <h1 className="flex items-center gap-2 text-xl font-semibold text-stone-900">
                <Percent className="h-5 w-5 shrink-0 text-stone-500" />
                Fee Configuration
              </h1>
              <p className="text-sm text-stone-500">
                Review fee settings for player deposits and player withdrawals. Fees are recorded in the wallet ledger for tracking and reconciliation.
              </p>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 disabled:opacity-60"
              onClick={() => void loadPageData()}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <FeeResponsibilityGuide />

          <div className="flex gap-2 border-b border-stone-200 px-4 py-4 sm:px-6">
            <button
              type="button"
              onClick={() => setActiveOperationTab('deposit')}
              className={`inline-flex min-w-[130px] items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                activeOperationTab === 'deposit'
                  ? 'bg-stone-900 text-white shadow-sm'
                  : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
              }`}
            >
              <ArrowDownToLine className="h-4 w-4" />
              Deposit
            </button>
            <button
              type="button"
              onClick={() => setActiveOperationTab('withdrawal')}
              className={`inline-flex min-w-[130px] items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                activeOperationTab === 'withdrawal'
                  ? 'bg-amber-600 text-white shadow-sm hover:bg-amber-700'
                  : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
              }`}
            >
              <ArrowUpFromLine className="h-4 w-4" />
              Withdrawal
            </button>
          </div>

          <div className="space-y-5 bg-white px-4 py-5 sm:px-6">
            {loading && rows.length === 0 ? (
              <p className="text-sm text-stone-500">Loading…</p>
            ) : groupedRowsByProvider.length === 0 ? (
              <p className="text-sm text-stone-500">{emptyMessage}</p>
            ) : (
              groupedRowsByProvider.map(([provider, providerRows]) => {
                const groupTone =
                  activeOperationTab === 'withdrawal'
                    ? {
                        border: 'border-amber-200',
                        header: 'border-amber-200 bg-amber-50 text-amber-800',
                        surface: 'bg-amber-50/20',
                      }
                    : {
                        border: 'border-blue-200',
                        header: 'border-blue-200 bg-blue-50 text-blue-800',
                        surface: 'bg-blue-50/20',
                      };
                return (
                <div key={provider} className={`overflow-hidden rounded-lg border ${groupTone.border} ${groupTone.surface}`}>
                  <div className={`border-b px-4 py-2 text-sm font-semibold uppercase tracking-wide ${groupTone.header}`}>
                    {formatProviderDisplayName(provider)}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed text-sm">
                      <thead>
                        <tr className="border-b border-stone-200 text-left text-xs text-stone-500">
                          <th className="w-[280px] px-4 py-3 font-medium whitespace-nowrap">Currency / Network</th>
                          <FeeSortTh
                            label="Gateway processing fee"
                            field="gateway"
                            sort={feeSort}
                            onSort={toggleFeeSort}
                            className="w-[180px] px-4 py-3 text-right whitespace-nowrap"
                          />
                          <FeeSortTh
                            label="Network fee"
                            field="network"
                            sort={feeSort}
                            onSort={toggleFeeSort}
                            className="w-[130px] px-4 py-3 text-right whitespace-nowrap"
                          />
                          <FeeSortTh
                            label="System fee"
                            field="system"
                            sort={feeSort}
                            onSort={toggleFeeSort}
                            className="w-[110px] px-4 py-3 text-right whitespace-nowrap"
                          />
                          <FeeSortTh
                            label="Total Fee"
                            field="total"
                            sort={feeSort}
                            onSort={toggleFeeSort}
                            className="w-[130px] px-4 py-3 text-right whitespace-nowrap"
                          />
                          <th className="w-[90px] px-4 py-3 text-center font-medium whitespace-nowrap">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {providerRows.map((row) => {
                          return (
                            <tr
                              key={providerFeeRowId(row)}
                              className="border-b border-stone-100 last:border-0 hover:bg-stone-50/80"
                            >
                              <td className="px-4 py-3">
                                <MethodCell row={row} supportedByKey={supportedByKey} />
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-stone-500">
                                {row.paymentGateFeePercent}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-stone-500">
                                {formatNetworkFeeDisplay(
                                  row.networkFeeType,
                                  row.networkFeePercent,
                                  row.networkFeeFixedUsd
                                )}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-stone-500">
                                {row.systemFeePercent}
                              </td>
                              <td className="px-4 py-3 text-right font-medium tabular-nums text-stone-700">
                                {formatTotalFeeDisplay(row)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                  row.isEnabled !== false
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-stone-100 text-stone-500'
                                }`}>
                                  {row.isEnabled !== false ? 'Enabled' : 'Disabled'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
              })
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
